import * as jwt from 'jwt-decode';
import * as R from 'ramda';
import Log from '../../common/logging';

import { Token, TokenResult } from '../../services/common/token';

/** Result callback for performing a token renewal */
export type TokenRenewalResult = (tokenResult: TokenResult) => void;
/** Callback function to request a token renewal. Waits for result callback */
export type RequestTokenRenewal = (resultCallback: TokenRenewalResult) => void;

/** Fallback expiry of 20 minutes */
const defaultExpirySeconds = 1200;

/** Renew token if it is within 5 minutes of expiry */
const tokenRenewalWindowSeconds = 300;

const currentEpochSeconds = (): number => {
  return Date.now() / 1000;
};

const calculateTokenExpiry = (expiryEpochSeconds?: number): number => {
  const tokenExpiry = expiryEpochSeconds || currentEpochSeconds() + defaultExpirySeconds;
  return Math.floor(tokenExpiry - tokenRenewalWindowSeconds);
};

const isTokenValid = (tokenExpiryEpochSeconds: number): boolean => {
  return tokenExpiryEpochSeconds - currentEpochSeconds() >= 0;
};

export default abstract class BaseToken implements Token {
  token: string;

  tokenExpiryTimestampEpochSeconds: number;

  constructor(token: string) {
    this.token = token;
    this.tokenExpiryTimestampEpochSeconds = BaseToken.GetTokenExpiryTimestamp(token);
  }

  getDecoded(): jwt.JwtPayload {
    return jwt.default<jwt.JwtPayload>(this.token);
  }

  setToken(token: string): void {
    this.token = token;
    this.tokenExpiryTimestampEpochSeconds = BaseToken.GetTokenExpiryTimestamp(token);
  }

  private static GetTokenExpiryTimestamp(token: string): number {
    let tokenExpiry: number;
    try {
      const decoded = jwt.default<jwt.JwtPayload>(token);
      tokenExpiry = calculateTokenExpiry(decoded.exp);
    } catch (error) {
      Log.error('Unexpected error decoding JWT', error as Error);
      // calculate using default expiry
      tokenExpiry = calculateTokenExpiry();
    }

    Log.debug(`Setting token expiry: ${new Date(tokenExpiry * 1000)}`);
    return tokenExpiry;
  }

  /** Returns true if the current token is valid and not expired */
  protected isTokenValid(): boolean {
    return (
      // If we have a token value
      !R.isNil(this.token) &&
      !R.isEmpty(this.token) &&
      // And it has not expired yet
      isTokenValid(this.tokenExpiryTimestampEpochSeconds)
    );
  }

  abstract getToken(): Promise<TokenResult>;
}
